import { ReactElement, ReactNode } from "react";
import { Assessment } from "types/assessmentTypes";
import PdfPageShell from "./PdfPageShell";
import RadarChartComponent from "../charts/RadarChartComponent";
import IndustryLifeCycle from "../industryLifeCycle/IndustryLifeCycle";
import StrategyReport, { StrategySection } from "../strategy/StrategyReport";
import PAEISection from "../PAEISection";
import ScoringResultsTable from "../ScoringResultsTable";
import PerformanceChartsPdf from "../chartsTab/PerformanceChartsPdf";
import ProcessStepsPage from "./ProcessStepsPage";
import { A4_HEIGHT_PX, PDF_SAFE_ZONE } from "./pdfConstants";

export interface PdfLabels {
  pages: {
    companyInfo: string;
    scoringTable: string;
    paei: string;
    radar: string;
    lifeCycle: string;
    performance: string;
    strategy: string;
    processSteps: string;
  };
  company: {
    companyName: string;
    nationalId: string;
    user: string;
    organization: string;
    industry: string;
    establishedYear: string;
    completedAt: string;
    pdfDownloadedAt: string;
    employeeCount: string;
    companyType: string;
    companyStage: string;
    licenseType: string;
  };
  companyValues: {
    companyName: string;
    nationalId: string;
    user: string;
    organization: string;
    industry: string;
    establishedYear: string;
    completedAt: string;
    pdfDownloadedAt: string;
    employeeCount: string;
    companyType: string;
    companyStage: string;
    licenseType: string;
  };
  scoringHeaders: {
    row: string;
    criteria: string;
    likert: string;
    objective: string;
    factor: string;
    corrected: string;
    interpretation: string;
  };
  statusLabels: {
    needsImprovement: string;
    average: string;
    good: string;
  };
  getCriteriaLabel: (index: number) => string;
  radarTitle: string;
  radarDesc: string;
  t?: (key: string, fallback?: string) => string;
}

export interface BuildPdfOptions {
  includePremium: boolean;
  backgroundImage?: string;
  dir?: "rtl" | "ltr";
  scoringRowsPerPage?: number;
  sectionTitles?: Partial<
    Record<
      | "companyInfo"
      | "paei"
      | "scoringTable"
      | "radar"
      | "lifeCycle"
      | "performanceOverview"
      | "financial"
      | "paeiGroup"
      | "infrastructure"
      | "market"
      | "sustainability"
      | "strategyPart1"
      | "strategyPart2"
      | "processSteps",
      string
    >
  >;
}

function ScaledFit({
  children,
  maxHeight = 800,
  scale = 0.92,
}: {
  children: ReactNode;
  maxHeight?: number;
  scale?: number;
}) {
  return (
    <div
      style={{
        height: maxHeight,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          width: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function CompanyInfoPage({ labels }: { labels: PdfLabels }) {
  const infoCards = [
    {
      key: "user",
      label: labels.company.user,
      value: labels.companyValues.user,
      icon: "👤",
    },
    {
      key: "organization",
      label: labels.company.organization,
      value: labels.companyValues.organization,
      icon: "🌐",
    },
    {
      key: "industry",
      label: labels.company.industry,
      value: labels.companyValues.industry,
      icon: "🏭",
    },
    {
      key: "companyType",
      label: labels.company.companyType,
      value: labels.companyValues.companyType,
      icon: "🏷️",
    },
    {
      key: "companyStage",
      label: labels.company.companyStage,
      value: labels.companyValues.companyStage,
      icon: "📊",
    },
    {
      key: "establishedYear",
      label: labels.company.establishedYear,
      value: labels.companyValues.establishedYear,
      icon: "🗓️",
    },
    {
      key: "completedAt",
      label: labels.company.completedAt,
      value: labels.companyValues.completedAt,
      icon: "✅",
    },
    {
      key: "employeeCount",
      label: labels.company.employeeCount,
      value: labels.companyValues.employeeCount,
      icon: "👥",
    },
    {
      key: "licenseType",
      label: labels.company.licenseType,
      value: labels.companyValues.licenseType,
      icon: "📜",
    },
    {
      key: "pdfDownloadedAt",
      label: labels.company.pdfDownloadedAt,
      value: labels.companyValues.pdfDownloadedAt,
      icon: "📥",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Hero */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 24,
          padding: "34px 38px",
          background:
            "linear-gradient(135deg, #0B4F4A 0%, #0F5C5A 30%, #1A7A76 65%, #2DA89A 100%)",
          color: "#fff",
          boxShadow:
            "0 20px 60px rgba(15,92,90,0.35), 0 4px 12px rgba(15,92,90,0.2)",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -100,
            left: -50,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 30,
            width: 60,
            height: 60,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.1)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <div
            style={{
              fontSize: 13,
              opacity: 0.9,
              letterSpacing: 3,
              fontWeight: 700,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 28,
                height: 2.5,
                background: "rgba(255,255,255,0.5)",
                borderRadius: 2,
              }}
            />
          </div>

          <div
            style={{
              marginTop: 16,
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: 0.5,
              lineHeight: 1.3,
              textShadow: "0 2px 8px rgba(0,0,0,0.15)",
              textAlign: "center",
            }}
          >
            {labels.companyValues.companyName || "-"}
          </div>

          <div
            style={{
              marginTop: 18,
              display: "inline-flex",
              alignItems: "center",
              textAlign: "center",
              gap: 10,
              padding: "10px 20px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <span style={{ fontSize: 16, opacity: 0.9 }}>🆔</span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: 0.5,
                opacity: 0.9,
              }}
            >
              {labels.company.nationalId}:
            </span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: 1,
              }}
            >
              {labels.companyValues.nationalId || "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
        }}
      >
        {infoCards.map((r, i) => (
          <div
            key={i}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 16,
              borderRadius: 18,
              border: "1px solid #E8EDF5",
              background:
                "linear-gradient(145deg, #FFFFFF 0%, #F9FBFD 50%, #F1F5F9 100%)",
              padding: "18px 20px",
              boxShadow:
                "0 4px 16px rgba(15,27,51,0.04), 0 1px 3px rgba(15,27,51,0.02)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 5,
                height: "100%",
                background: "linear-gradient(180deg, #2DA89A 0%, #1A7A76 100%)",
                borderRadius: "0 18px 18px 0",
              }}
            />

            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                flexShrink: 0,
                boxShadow: "0 2px 8px rgba(99,102,241,0.08)",
              }}
            >
              {r.icon}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#64748B",
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {r.label}
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#0F1B33",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                }}
              >
                {r.value || "-"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

export function buildAssessmentPdfPages(
  assessment: Assessment,
  labels: PdfLabels,
  options: BuildPdfOptions
): ReactElement[] {
  const criteriaBreakdown =
    assessment.scoringResults?.[0]?.criteriaBreakdown || [];

  const CONTENT_HEIGHT =
    A4_HEIGHT_PX - PDF_SAFE_ZONE.top - PDF_SAFE_ZONE.bottom;

  const dir = options.dir ?? "rtl";
  const titles = options.sectionTitles ?? {};
  const rowsPerPage = options.scoringRowsPerPage ?? 18;
  const processSteps = assessment.company?.processSteps ?? [];
  const STEPS_PER_PAGE = 6;

  type Draft = {
    content: ReactElement;
    bleed?: boolean;
    title?: string;
    dark?: boolean;
  };

  const drafts: Draft[] = [];

  // 1) Cover
  drafts.push({ bleed: true, content: <div /> });

  // 2) Company info
  drafts.push({
    content: <CompanyInfoPage labels={labels} />,
    title: titles.companyInfo ?? labels.pages.companyInfo,
  });

  if (processSteps.length > 0) {
    const stepPages = chunk(processSteps, STEPS_PER_PAGE);
    stepPages.forEach((pageSteps, pageIdx) => {
      drafts.push({
        content: (
          <ScaledFit maxHeight={CONTENT_HEIGHT} scale={0.99}>
            <ProcessStepsPage
              steps={pageSteps}
              dir={dir}
              emptyLabel={labels.pages.processSteps ?? "-"}
            />
          </ScaledFit>
        ),
        title:
          (titles.processSteps ?? labels.pages.processSteps) +
          (stepPages.length > 1 ? ` (${pageIdx + 1}/${stepPages.length})` : ""),
      });
    });
  }

  // 3) PAEI
  drafts.push({
    content: (
      <ScaledFit maxHeight={CONTENT_HEIGHT} scale={0.99}>
        <PAEISection
          criteriaBreakdown={criteriaBreakdown}
          company={assessment.company}
          pdfMode
        />
      </ScaledFit>
    ),
    title: titles.paei ?? labels.pages.paei,
  });

  // 4,5) Scoring table
  const totalCriteria = [...criteriaBreakdown].sort(
    (a, b) => a.criteriaIndex - b.criteriaIndex
  ).length;
  const scoringPages = Math.max(1, Math.ceil(totalCriteria / rowsPerPage));
  for (let p = 0; p < scoringPages; p++) {
    drafts.push({
      content: (
        <ScaledFit maxHeight={CONTENT_HEIGHT} scale={0.92}>
          <ScoringResultsTable
            scoringResults={assessment.scoringResults || []}
            rowsPerPage={18}
            pageIndex={p}
            scopeOverride="user"
            compact
          />
        </ScaledFit>
      ),
      title: titles.scoringTable ?? labels.pages.scoringTable,
    });
  }

  // 6) Radar
  drafts.push({
    content: (
      <ScaledFit maxHeight={CONTENT_HEIGHT} scale={0.99}>
        <RadarChartComponent
          criteriaBreakdown={criteriaBreakdown}
          title={labels.radarTitle}
          description={labels.radarDesc}
          height={700}
        />
      </ScaledFit>
    ),
    title: titles.radar ?? labels.pages.radar,
  });

  // 7) Life-cycle
  drafts.push({
    content: (
      <ScaledFit maxHeight={CONTENT_HEIGHT} scale={0.92}>
        <IndustryLifeCycle
          scoringResults={assessment.scoringResults || []}
          company={assessment.company}
          disableAnimation
          pdfMode
        />
      </ScaledFit>
    ),
    title: titles.lifeCycle ?? labels.pages.lifeCycle,
  });

  if (options.includePremium) {
    // 8,9,10) PerformanceChartsPdf
    drafts.push({
      content: (
        <PerformanceChartsPdf
          assessment={assessment}
          items={["overview", "financial"]}
        />
      ),
      title: titles.performanceOverview ?? labels.pages.performance,
      dark: true,
    });

    drafts.push({
      content: (
        <PerformanceChartsPdf
          assessment={assessment}
          items={["paei", "infrastructure"]}
        />
      ),
      title: titles.paeiGroup ?? labels.pages.performance,
      dark: true,
    });

    drafts.push({
      content: (
        <PerformanceChartsPdf
          assessment={assessment}
          items={["market", "sustainability"]}
        />
      ),
      title: titles.market ?? labels.pages.performance,
      dark: true,
    });

    // 11) Strategy
    const strategyAll: StrategySection[] = ["scores", "primary", "final"];
    drafts.push({
      content: (
        <div
          style={{
            width: "100%",
            height: 800,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "100%",
              transformOrigin: "top center",
              transform: "scale(0.77)",
            }}
          >
            <StrategyReport
              criteriaBreakdown={criteriaBreakdown}
              company={assessment.company}
              sections={strategyAll}
              pdfMode
            />
          </div>
        </div>
      ),
      title: titles.strategyPart1 ?? labels.pages.strategy,
    });
  }

  const totalPages = drafts.length;

  return drafts.map((draft, index) => (
    <PdfPageShell
      key={`pdf-page-${index}`}
      pageNumber={draft.bleed ? undefined : index + 1}
      totalPages={draft.bleed ? undefined : totalPages}
      backgroundImage={options.backgroundImage}
      bleed={draft.bleed}
      title={draft.title}
      dir={dir}
      dark={draft.dark}
    >
      {draft.content}
    </PdfPageShell>
  ));
}
